import { type ReactNode } from 'react';
import { ApiError } from '../api/client';

// ---------- Словари статусов ----------

const STAGE_LABELS: Record<string, string> = {
  new: 'Новая', calculation: 'Просчёт', offer: 'Предложение', agreed: 'Согласована',
  in_progress: 'В работе', ready: 'Готова', success: 'Успешна', refused: 'Отказ', cancelled: 'Отменена',
};
const GENERIC_LABELS: Record<string, string> = {
  draft: 'Черновик', active: 'Активен', archived: 'В архиве', open: 'Открыт',
  in_progress: 'В работе', done: 'Завершено', overdue: 'Просрочено', cancelled: 'Отменено',
  resolved: 'Решён', escalated: 'Эскалирован', sent: 'Отправлен', answered: 'Получен ответ',
  confirmed: 'Подтверждена', expired: 'Истекла', accepted: 'Принято', rejected: 'Отклонено',
  planned: 'Запланирован', launched: 'Запущен', completed: 'Завершён',
  ss: 'СС', ds: 'ДС', refusal_letter: 'Отказное письмо', aip: 'АИП', sgr: 'СГР',
  other: 'Другое', unknown: 'Не определено',
  payer: 'Плательщик', applicant: 'Заявитель', manufacturer: 'Изготовитель',
  primary: 'Основной', internal: 'Внутренний', client: 'Клиентский', all: 'Все',
  control_event: 'Событие', message: 'Сообщение',
  person: 'Физлицо', company: 'Организация',
  // Статусы платежей и банковских операций (Stage 5)
  pending: 'Ожидает', received: 'Получена', matched: 'Сопоставлена',
  allocated: 'Распределена', conflict: 'Конфликт',
  // Статусы актов, подписи/оригинала и закрытия (Stage 6)
  issued: 'Выставлен', signed: 'Подписан', lost: 'Утерян',
  closed: 'Закрыта', reopened: 'Повторно открыта', paid: 'Оплачен',
  manual: 'Вручную',
  // === WT3 docs ===: типы генерируемых документов ТЗ §10.
  application: 'Заявление', du: 'ДУ', poa: 'Доверенность',
};

export function label(value?: string | null): string {
  if (!value) return '—';
  return STAGE_LABELS[value] ?? GENERIC_LABELS[value] ?? value;
}

const TONE_BY_STATUS: Record<string, string> = {
  new: 'info', calculation: 'warn', offer: 'warn', agreed: 'ok', success: 'ok',
  refused: 'bad', cancelled: 'muted', in_progress: 'info', ready: 'ok',
  draft: 'muted', active: 'info', open: 'info', done: 'ok', overdue: 'bad',
  resolved: 'ok', escalated: 'bad', sent: 'info', answered: 'ok',
  confirmed: 'ok', expired: 'muted', accepted: 'ok', rejected: 'bad',
  planned: 'muted', launched: 'info', completed: 'ok',
  pending: 'warn', received: 'info', matched: 'info', allocated: 'ok',
  conflict: 'bad',
  issued: 'info', signed: 'ok', lost: 'bad',
  closed: 'ok', reopened: 'warn', paid: 'ok', manual: 'warn',
};

export function StatusBadge({ value }: { value?: string | null }) {
  if (!value) return <span className="badge muted">—</span>;
  const tone = TONE_BY_STATUS[value] ?? 'muted';
  return <span className={`badge ${tone}`}>{label(value)}</span>;
}

// ---------- Форматирование ----------

export function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtMoney(value?: string | number | null, currency?: string | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  const cur = currency ?? '';
  if (Number.isNaN(n)) return `${value} ${cur}`.trim();
  return `${n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`.trim();
}

export function shortId(id?: string | null): string {
  if (!id) return '—';
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

// ---------- Базовые компоненты ----------

export function PageHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="page-header">
      <h1>{title}</h1>
      <div className="page-header-actions">{children}</div>
    </div>
  );
}

export function Card({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-header">
          {title && <h2>{title}</h2>}
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

export function Loading({ text = 'Загрузка…' }: { text?: string }) {
  return <div className="loading">{text}</div>;
}

export function Empty({ text = 'Нет данных' }: { text?: string }) {
  return <div className="empty">{text}</div>;
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  if (error instanceof ApiError) {
    if (error.isForbidden) {
      return (
        <div className="alert alert-forbidden">
          <strong>Доступ запрещён.</strong>
          <span>У вашей учётной записи нет прав на этот раздел или действие.</span>
          {onRetry && <button className="btn" onClick={onRetry}>Повторить</button>}
        </div>
      );
    }
    if (error.isConflict) {
      return (
        <div className="alert alert-error">
          <strong>Конфликт версий (409).</strong>
          <span>Данные были изменены другим пользователем. Обновите страницу и повторите действие.</span>
          {onRetry && <button className="btn" onClick={onRetry}>Обновить</button>}
        </div>
      );
    }
    if (error.isNotFound) {
      return (
        <div className="alert alert-warn">
          <strong>Не найдено.</strong>
          <span>{error.message}</span>
          {onRetry && <button className="btn" onClick={onRetry}>Повторить</button>}
        </div>
      );
    }
    return (
      <div className="alert alert-error">
        <strong>Ошибка{error.status ? ` ${error.status}` : ''}.</strong>
        <span>{error.message}</span>
        {error.correlationId && <span className="muted">ID: {error.correlationId}</span>}
        {onRetry && <button className="btn" onClick={onRetry}>Повторить</button>}
      </div>
    );
  }
  const msg = error instanceof Error ? error.message : String(error);
  return (
    <div className="alert alert-error">
      <strong>Ошибка.</strong><span>{msg}</span>
      {onRetry && <button className="btn" onClick={onRetry}>Повторить</button>}
    </div>
  );
}

export interface Column<T> {
  key: string;
  title: string;
  render?: (row: T) => ReactNode;
  width?: string;
}

export function DataTable<T extends { id?: string | number }>({
  columns, rows, onRowClick, empty = 'Нет данных', className = '',
}: {
  columns: Column<T>[];
  rows: T[] | null | undefined;
  onRowClick?: (row: T) => void;
  empty?: string;
  className?: string;
}) {
  if (!rows) return <Loading />;
  if (rows.length === 0) return <Empty text={empty} />;
  return (
    <div className="table-wrap">
      <table className={`table ${className}`.trim()}>
        <thead>
          <tr>{columns.map((c) => <th key={c.key} style={c.width ? { width: c.width } : undefined}>{c.title}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id ?? i} onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'clickable' : undefined}>
              {columns.map((c) => (
                <td key={c.key} className={`cell-${c.key}`}>{c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '—')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Field({ label: text, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span className="field-label">{text}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function KV({ k, v }: { k: string; v?: ReactNode }) {
  return (
    <div className="kv">
      <span className="kv-key">{k}</span>
      <span className="kv-value">{v ?? '—'}</span>
    </div>
  );
}

export function Tabs({ tabs, active, onChange }: {
  tabs: { key: string; title: string }[];
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="tabs">
      {tabs.map((t) => (
        <button key={t.key}
                className={`tab ${t.key === active ? 'active' : ''}`}
                onClick={() => onChange(t.key)}>
          {t.title}
        </button>
      ))}
    </div>
  );
}

export function JsonBlock({ data }: { data: unknown }) {
  if (data === null || data === undefined) return <Empty text="Нет данных" />;
  return <pre className="json-block">{JSON.stringify(data, null, 2)}</pre>;
}
